(function () {
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initPdpReveal() {
    if (!document.body || !document.body.classList.contains('template-product-magbak-redesign')) {
      return;
    }

    var targets = Array.prototype.slice.call(document.querySelectorAll([
      '.mb-product-details__tile',
      '.mb-proof__grid',
      '.mb-proof-card',
      '.mb-proof-comparison',
      '.mb-proof__visual',
      '.mb-pdp-section .mb-pdp-tile',
      '.mb-pdp-section.mb-pdp-layout--enclosed > .page-width',
      '.mb-data-table .mb-pdp-table',
      '.mb-reviews__wrap',
      // Homepage-specific sections that use their own markup (not .mb-pdp-tile)
      // and were therefore missed by the reveal. Each fades in as one unit.
      '.mb-pdp-collection-list',
      '.mb-pdp-featured-products',
      '.mb-trust-strip'
    ].join(',')));

    if (!targets.length) {
      return;
    }

    var canObserve = 'IntersectionObserver' in window && !prefersReducedMotion();

    var observer = canObserve
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) {
              return;
            }

            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        }, {
          // Trigger exactly at viewport entry: the fade plays while the tile
          // scrolls in, so the animation is visible but opacity is already
          // ramping as it appears (no blank viewports). Pre-arming earlier
          // (35%) finished the fade off-screen; arming later (-12%) left
          // whole viewports blank at normal scroll speed.
          rootMargin: '0px 0px 0px 0px',
          threshold: 0.01
        })
      : null;

    targets.forEach(function (target, index) {
      if (target.dataset.mbPdpReveal === 'ready') {
        return;
      }

      target.dataset.mbPdpReveal = 'ready';

      // Targets already in the viewport must paint immediately: starting them
      // at opacity 0 gates the hero poster's first paint behind a transition
      // frame, which delays LCP and leaves the first viewport blank.
      if (target.getBoundingClientRect().top < window.innerHeight) {
        return;
      }

      target.style.setProperty('--mb-reveal-index', index % 6);
      target.classList.add('mb-pdp-reveal');

      if (!observer) {
        target.classList.add('is-visible');
        return;
      }

      observer.observe(target);
    });
  }

  function pausePdpVideoAutomatically(video) {
    if (!video || video.paused) {
      return;
    }

    video.dataset.mbPdpAutopausing = 'true';
    video.pause();
  }

  function initPdpVideoAutopause() {
    if (!document.body || !document.body.classList.contains('template-product-magbak-redesign')) {
      return;
    }

    var videos = Array.prototype.slice.call(document.querySelectorAll('[data-mb-pdp-autopause="true"] video'));
    if (!videos.length || !('IntersectionObserver' in window)) {
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        if (entry.isIntersecting) {
          if (video.autoplay && video.dataset.mbPdpUserPaused !== 'true') {
            video.play().catch(function () {});
          }
          return;
        }

        pausePdpVideoAutomatically(video);
      });
    }, {
      threshold: 0.08
    });

    videos.forEach(function (video) {
      if (video.dataset.mbPdpAutopause === 'ready') {
        return;
      }

      video.dataset.mbPdpAutopause = 'ready';
      video.addEventListener('play', function () {
        video.dataset.mbPdpUserPaused = 'false';
      });
      video.addEventListener('pause', function () {
        if (video.dataset.mbPdpAutopausing === 'true') {
          video.dataset.mbPdpAutopausing = 'false';
          return;
        }

        if (video.ended) {
          return;
        }

        video.dataset.mbPdpUserPaused = 'true';
      });
      observer.observe(video);
    });
  }

  function isAppleHlsBrowser() {
    var ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) {
      return true;
    }
    // Desktop/iPadOS Safari. Chrome reports HLS support via canPlayType but
    // stalls on Shopify's manifests, so this must stay a UA check, not a
    // capability check.
    return /Macintosh/.test(ua) && /Safari\//.test(ua) && !/Chrome|Chromium|Edg\//.test(ua);
  }

  function loadLazyVideo(video) {
    if (!video || video.dataset.mbLazyVideoLoaded === 'true') {
      return;
    }

    var hasSource = false;
    if (video.dataset.mbHlsSrc && isAppleHlsBrowser() && !video.getAttribute('src')) {
      video.setAttribute('src', video.dataset.mbHlsSrc);
      hasSource = true;
    }

    if (video.dataset.src && !video.getAttribute('src')) {
      video.setAttribute('src', video.dataset.src);
      hasSource = true;
    }

    video.querySelectorAll('source[data-src]').forEach(function (source) {
      source.setAttribute('src', source.dataset.src);
      source.removeAttribute('data-src');
      hasSource = true;
    });

    if (!hasSource) {
      return;
    }

    video.dataset.mbLazyVideoLoaded = 'true';
    video.load();
    if (video.autoplay && video.dataset.mbPdpUserPaused !== 'true') {
      video.play().catch(function () {});
    }
  }

  function hydrateEagerVideo(video) {
    if (video.dataset.mbEagerVideoQueued === 'true') {
      return;
    }

    video.dataset.mbEagerVideoQueued = 'true';

    // Respect users who asked for less data; the poster carries the design.
    // Everyone else streams only after window load so the video never
    // competes with CSS/poster bandwidth before first paint. Apple devices
    // get adaptive HLS (see loadLazyVideo); others download the mp4
    // rendition, so keeping hero uploads near 720p matters.
    var connection = navigator.connection;
    if (connection && (connection.saveData || /(^|-)2g/.test(connection.effectiveType || ''))) {
      return;
    }

    if (document.readyState === 'complete') {
      loadLazyVideo(video);
      return;
    }

    window.addEventListener('load', function () {
      loadLazyVideo(video);
    }, { once: true });
  }

  function initPdpLazyVideos() {
    if (!document.body || !document.body.classList.contains('template-product-magbak-redesign')) {
      return;
    }

    var allVideos = Array.prototype.slice.call(document.querySelectorAll('video[data-mb-lazy-video]'));
    if (!allVideos.length) {
      return;
    }

    var videos = [];
    allVideos.forEach(function (video) {
      if (video.dataset.mbEagerVideo !== undefined) {
        hydrateEagerVideo(video);
      } else {
        videos.push(video);
      }
    });

    if (!videos.length) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      videos.forEach(loadLazyVideo);
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          return;
        }

        loadLazyVideo(entry.target);
        observer.unobserve(entry.target);
      });
    }, {
      rootMargin: '700px 0px',
      threshold: 0.01
    });

    videos.forEach(function (video) {
      if (video.dataset.mbLazyVideoObserver === 'ready' || video.dataset.mbLazyVideoLoaded === 'true') {
        return;
      }

      video.dataset.mbLazyVideoObserver = 'ready';
      observer.observe(video);
    });
  }

  function initPdpExternalVideoPosters() {
    if (!document.body || !document.body.classList.contains('template-product-magbak-redesign')) {
      return;
    }

    document.querySelectorAll('[data-mb-pdp-external-video]').forEach(function (trigger) {
      if (trigger.dataset.mbPdpExternalVideoReady === 'true') {
        return;
      }

      trigger.dataset.mbPdpExternalVideoReady = 'true';
      trigger.addEventListener('click', function () {
        var src = trigger.dataset.mbPdpVideoSrc;
        if (!src) {
          return;
        }

        var iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.title = trigger.dataset.mbPdpVideoTitle || 'Video';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.loading = 'eager';
        trigger.replaceWith(iframe);
      });
    });
  }

  function initReducedMotionMedia() {
    if (!prefersReducedMotion()) {
      return;
    }

    document.querySelectorAll('.mb-redesign-site-shell video[autoplay]').forEach(function (video) {
      video.removeAttribute('autoplay');
      video.autoplay = false;
      video.pause();
    });
  }

  function normalizeSku(value) {
    return String(value || '').trim().toLowerCase();
  }

  function skuList(value) {
    return String(value || '')
      .split(',')
      .map(normalizeSku)
      .filter(Boolean);
  }

  function sectionMatchesSku(section, sku) {
    var allowedSkus = skuList(section.dataset.mbPdpSectionSkus);
    if (!allowedSkus.length) {
      return true;
    }

    return allowedSkus.indexOf(normalizeSku(sku)) !== -1;
  }

  function pauseSectionMedia(section) {
    section.querySelectorAll('video').forEach(function (video) {
      pausePdpVideoAutomatically(video);
    });
  }

  function updateSkuFilteredSections(detail) {
    if (!document.body || !document.body.classList.contains('template-product-magbak-redesign')) {
      return;
    }

    var sku = detail && (detail.sku || detail.variant && detail.variant.sku);
    if (!detail || (!('variant' in detail) && !('sku' in detail))) {
      return;
    }
    document.querySelectorAll('[data-mb-pdp-sku-section]').forEach(function (section) {
      var isVisible = sectionMatchesSku(section, sku);
      section.hidden = !isVisible;
      section.setAttribute('aria-hidden', String(!isVisible));
      section.classList.toggle('is-sku-hidden', !isVisible);
      if (!isVisible) {
        pauseSectionMedia(section);
      }
    });
  }

  function initSkuFilteredSections() {
    updateSkuFilteredSections(window.MagBakPdpVariant || {});
  }

  function footerStickyOffset() {
    var offset = 24;
    document.querySelectorAll('.mb-clean-sticky-atc, [class*="discount"], [class*="promo"]').forEach(function (element) {
      var styles = window.getComputedStyle(element);
      if (styles.position !== 'fixed' || styles.display === 'none' || styles.visibility === 'hidden') {
        return;
      }

      var rect = element.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom > window.innerHeight - 160) {
        offset = Math.max(offset, window.innerHeight - rect.top + 24);
      }
    });

    return offset;
  }

  function keepFooterGroupAboveSticky(group) {
    if (!group.open) {
      return;
    }

    window.requestAnimationFrame(function () {
      var rect = group.getBoundingClientRect();
      if (rect.top >= window.innerHeight || rect.bottom <= 0) {
        return;
      }

      var safeBottom = window.innerHeight - footerStickyOffset();
      if (rect.bottom <= safeBottom) {
        return;
      }

      window.scrollBy({
        top: rect.bottom - safeBottom,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    });
  }

  function initFooterAccordions() {
    document.querySelectorAll('.mb-redesign-footer__group').forEach(function (group) {
      if (group.dataset.mbFooterAccordion === 'ready') {
        return;
      }

      group.dataset.mbFooterAccordion = 'ready';
      var shouldAdjustScroll = false;
      var summary = group.querySelector('.mb-redesign-footer__summary');

      if (summary) {
        summary.addEventListener('pointerdown', function () {
          shouldAdjustScroll = true;
        });
        summary.addEventListener('keydown', function (event) {
          if (event.code === 'Enter' || event.code === 'Space') {
            shouldAdjustScroll = true;
          }
        });
      }

      group.addEventListener('toggle', function () {
        if (!shouldAdjustScroll) {
          return;
        }
        shouldAdjustScroll = false;
        keepFooterGroupAboveSticky(group);
      });
    });
  }

  function initHeaderMenus() {
    document.querySelectorAll('[data-mb-header-cases-menu]').forEach(function (menu) {
      if (menu.dataset.mbHeaderCasesReady === 'true') {
        return;
      }

      menu.dataset.mbHeaderCasesReady = 'true';

      function activateBrand(brand) {
        if (!brand) {
          return;
        }

        menu.querySelectorAll('[data-mb-cases-brand]').forEach(function (button) {
          var isActive = button.dataset.mbCasesBrand === brand;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        menu.querySelectorAll('[data-mb-cases-panel]').forEach(function (panel) {
          var isActive = panel.dataset.mbCasesPanel === brand;
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });
      }

      menu.querySelectorAll('[data-mb-cases-brand]').forEach(function (button) {
        button.addEventListener('click', function () {
          activateBrand(button.dataset.mbCasesBrand);
        });
        button.addEventListener('mouseenter', function () {
          activateBrand(button.dataset.mbCasesBrand);
        });
        button.addEventListener('focus', function () {
          activateBrand(button.dataset.mbCasesBrand);
        });
      });
    });

    document.querySelectorAll('.mb-header-menu').forEach(function (details) {
      if (details.dataset.mbHeaderMenuReady === 'true') {
        return;
      }

      details.dataset.mbHeaderMenuReady = 'true';
      details.addEventListener('toggle', function () {
        if (!details.open) {
          return;
        }

        document.querySelectorAll('.mb-header-menu[open]').forEach(function (otherDetails) {
          if (otherDetails !== details) {
            otherDetails.open = false;
          }
        });
      });
    });
  }

  function initCollectionBrandFilters() {
    document.querySelectorAll('[data-mb-collection-brand-filter]').forEach(function (filter) {
      if (filter.dataset.mbCollectionBrandReady === 'true') {
        return;
      }

      filter.dataset.mbCollectionBrandReady = 'true';

      var section = filter.closest('.mb-collection');
      if (!section) {
        return;
      }

      var cards = Array.prototype.slice.call(section.querySelectorAll('[data-mb-collection-card-brand]'));
      var count = section.querySelector('.mb-collection__count');
      var empty = section.querySelector('[data-mb-collection-brand-empty]');
      var activeBrand = '';

      function labelForBrand(brand) {
        if (brand === 'iphone') {
          return 'iPhone cases';
        }
        if (brand === 'samsung') {
          return 'Samsung Galaxy cases';
        }
        if (brand === 'pixel') {
          return 'Google Pixel cases';
        }
        return 'products';
      }

      function applyBrand(nextBrand) {
        activeBrand = activeBrand === nextBrand ? '' : nextBrand;

        filter.querySelectorAll('[data-mb-collection-brand]').forEach(function (button) {
          var isActive = button.dataset.mbCollectionBrand === activeBrand;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        var visibleCount = 0;
        cards.forEach(function (card) {
          var shouldShow = !activeBrand || card.dataset.mbCollectionCardBrand === activeBrand;
          card.hidden = !shouldShow;
          card.classList.toggle('is-hidden-by-brand', !shouldShow);
          if (shouldShow) {
            visibleCount += 1;
          }
        });

        if (count) {
          count.textContent = activeBrand ? visibleCount + ' ' + labelForBrand(activeBrand) : cards.length + ' products';
        }

        if (empty) {
          empty.hidden = visibleCount !== 0;
        }
      }

      filter.querySelectorAll('[data-mb-collection-brand]').forEach(function (button) {
        button.addEventListener('click', function () {
          applyBrand(button.dataset.mbCollectionBrand);
        });
      });
    });
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('.mb-header-menu')) {
      return;
    }

    document.querySelectorAll('.mb-header-menu[open]').forEach(function (details) {
      details.open = false;
    });
  });

  document.addEventListener('keyup', function (event) {
    if (event.code !== 'Escape') {
      return;
    }

    document.querySelectorAll('.mb-header-menu[open]').forEach(function (details) {
      details.open = false;
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initPdpReveal();
      initPdpLazyVideos();
      initPdpVideoAutopause();
      initPdpExternalVideoPosters();
      initReducedMotionMedia();
      initSkuFilteredSections();
      initFooterAccordions();
      initHeaderMenus();
      initCollectionBrandFilters();
    });
  } else {
    initPdpReveal();
    initPdpLazyVideos();
    initPdpVideoAutopause();
    initPdpExternalVideoPosters();
    initReducedMotionMedia();
    initSkuFilteredSections();
    initFooterAccordions();
    initHeaderMenus();
    initCollectionBrandFilters();
  }

  document.addEventListener('shopify:section:load', initPdpReveal);
  document.addEventListener('shopify:section:load', initPdpLazyVideos);
  document.addEventListener('shopify:section:load', initPdpVideoAutopause);
  document.addEventListener('shopify:section:load', initPdpExternalVideoPosters);
  document.addEventListener('shopify:section:load', initReducedMotionMedia);
  document.addEventListener('shopify:section:load', initSkuFilteredSections);
  document.addEventListener('shopify:section:load', initFooterAccordions);
  document.addEventListener('shopify:section:load', initHeaderMenus);
  document.addEventListener('shopify:section:load', initCollectionBrandFilters);
  document.addEventListener('mb:pdp:variant-change', function (event) {
    updateSkuFilteredSections(event.detail || {});
  });
})();

